import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {
  Box,
  Button,
  Card,
  CardContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  Chip,
  useTheme,
  alpha,
  LinearProgress,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import Loading from 'components/Loading';
import { BackButton } from 'components/Button';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PersonIcon from '@mui/icons-material/Person';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CloseIcon from '@mui/icons-material/Close';
import { confirmAsync } from '../../utils/functions/globalDialog';
import { formatEmployeeThaiName } from '../../utils/functions/employeeName';
import * as actions from '../../redux/actions';
import api from '../../utils/functions/api';

import { getBankLabel } from '../../constants/thaiBanks';
const payeeKey = (e) => {
  const p = e?.payee || {};
  const name = (p.name || '').toString().trim();
  const bank = (p.bank || '').toString().trim();
  const acc = (p.account_number || '').toString().trim();
  return `${name}|${bank}|${acc}`;
};

export default function NewPayment({ title, subtitle, onClose }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const theme = useTheme();
  const expenses = useSelector((state) => state.expenses);
  const me = useSelector((state) => state.me);

  const [creating, setCreating] = useState(false);
  // โครงการยกเว้นที่เลือกไว้ที่ /management/project-prefixes — null = ยังโหลดไม่เสร็จ, [] = ไม่มีค่า (ใช้ JSID_00001/2/3)
  const [bypassProjectIds, setBypassProjectIds] = useState(null);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    // ต้องผ่านการอนุมัติ "การจ่ายเงิน" (payment_approval_status=APPROVED) และ "ตรวจสอบ"
    // (review_status=REVIEWED) มาก่อนด้วย ไม่ใช่แค่ status เบิกเป็น APPROVE เฉยๆ — ต้องตรงกับเงื่อนไขที่
    // backend ใช้จริงตอนสร้าง (payment.service.js createPrepareByPayee) ไม่งั้น preview list กับของจริง
    // ที่กดสร้างได้จะไม่ตรงกัน
    dispatch(
      actions.expensesAll({
        status: 'APPROVE',
        paymentApprovalStatuses: 'APPROVED',
        reviewStatuses: 'REVIEWED',
        size: 99999,
        page: 1,
      }),
    );
    return () => {};
  }, [dispatch]);

  useEffect(() => {
    api.get(`${process.env.REACT_APP_API_URL}/information/`)
      .then((res) => setBypassProjectIds((res.data?.prepare_bypass_projects || []).map(String)))
      .catch(() => setBypassProjectIds([]));
  }, []);

  const groupedByPayee = useMemo(() => {
    const allRows = expenses?.rows || [];
    if (!allRows.length) return [];

    // เงื่อนไข: ต้องครบกำหนดจ่ายแล้ว — อิง e.expected_payment_date ตรงๆ (เหมือน sub-tab "จ่ายเงินวันนี้")
    // แทนการคำนวณ readyAt สดจาก approval_logs เอง ต้องตรงกับ backend createPrepareByPayee เป๊ะ — เทียบ
    // แค่ "ครบกำหนดภายในสิ้นวันนี้แล้วหรือยัง" (<= สิ้นวันนี้เวลาไทย) ไม่ใช่วันก่อนหน้าวันนี้ เพราะ
    // expected_payment_date เอง (ดู computeExpectedPaymentDate ใน backend models/Expenses.js) บวก
    // +1 วันจากวันอนุมัติไว้ให้แล้ว
    const endOfTodayTh = new Date(
      `${new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Bangkok' })}T23:59:59.999+07:00`,
    ).getTime();
    // โครงการที่ตั้งเบิกแล้วข้ามเงื่อนไขวันอนุมัติได้เสมอ — ต้องตรงกับ bypassMatch ใน backend
    // createPrepareByPayee เป๊ะ: มีโครงการที่เลือกไว้ = ใช้เฉพาะที่เลือก, ไม่มี = JSID_00001/2/3
    const isBypassProject = (project) => {
      if (bypassProjectIds?.length) return bypassProjectIds.includes(String(project?._id || project?.id));
      return ['JSID_00001', 'JSID_00002', 'JSID_00003'].includes(project?.project_number);
    };
    // ข้ามเงื่อนไขวันอนุมัติได้ทันทีถ้า: is_urgent (toggle "ด่วน" จาก modal รายละเอียด),
    // isLabor (รายการเบิกค่าแรง), isRegular (รายการเบิกประจำ), is_salary_advance (รายการเบิก CRL),
    // หรืออยู่ในโครงการที่ยกเว้นไว้ — ต้องตรงกับ backend createPrepareByPayee
    const rows = allRows.filter(
      (e) => e.is_urgent === true
        || e.isLabor === true
        || e.isRegular === true
        || e.is_salary_advance === true
        || isBypassProject(e.project)
        || (e.expected_payment_date && new Date(e.expected_payment_date).getTime() <= endOfTodayTh),
    );
    if (!rows.length) return [];
    const groups = _.groupBy(rows, payeeKey);
    return Object.entries(groups).map(([key, list]) => {
      const first = list[0];
      const payee = first?.payee || { name: '', bank: '', account_number: '' };
      const totalAmount = _.sumBy(
        list,
        (e) => (Number(e?.price) || 0) - (Number(e?.withholding_tax) || 0)
      );
      return {
        key,
        payee: {
          name: payee.name || '-',
          bank: payee.bank || '-',
          account_number: payee.account_number || '-',
        },
        count: list.length,
        totalAmount,
        expenses: list,
      };
    });
  }, [expenses?.rows, bypassProjectIds]);

  const formatMoney = (val) =>
    (val || 0).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');

  const handleCreatePrepare = async () => {
    if (groupedByPayee.length === 0) {
      setMessage({ type: 'warning', text: 'ไม่มีรายการที่อนุมัติแล้ว (APPROVE) เพื่อสร้างรายการเตรียมจ่าย' });
      return;
    }
    const confirm = await confirmAsync(
      `ต้องการสร้างรายการเตรียมจ่าย ${groupedByPayee.length} รายการ (ตามผู้รับเงิน) ใช่หรือไม่?`
    );
    if (!confirm) return;
    setCreating(true);
    setMessage(null);
    try {
      const result = await dispatch(
        actions.paymentPrepareByPayee({
          creater: me?.userData?._id,
          create_by: formatEmployeeThaiName(me?.userData),
        })
      );
      setMessage({
        type: 'success',
        text: `สร้างรายการเตรียมจ่ายแล้ว ${result?.created || 0} รายการ`,
      });
      dispatch(actions.paymentAll({ page: 1, size: 20 }));
      setTimeout(() => {
        if (onClose) {
          onClose(true);
        } else {
          history.push('/finance/Payment');
        }
      }, 1500);
    } catch (err) {
      setMessage({
        type: 'error',
        text: err?.message || 'สร้างรายการไม่สำเร็จ',
      });
    } finally {
      setCreating(false);
    }
  };

  const isLoading = expenses?.isLoading && !expenses?.rows?.length;
  if (isLoading) return <Loading isLoading />;

  return (
    <Box sx={{ py: 2, px: { xs: 1, sm: 2 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {onClose ? (
            <IconButton size="small" onClick={() => onClose(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          ) : (
            <BackButton />
          )}
          <Typography variant="h6" fontWeight={600}>
            สร้างรายการเตรียมจ่าย (ตามผู้รับเงิน)
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          disabled={creating || groupedByPayee.length === 0}
          startIcon={creating ? null : <PlaylistAddCheckIcon />}
          onClick={handleCreatePrepare}
        >
          {creating ? 'กำลังสร้าง...' : 'สร้างรายการเตรียมจ่ายทั้งหมด'}
        </Button>
      </Box>

      {message && (
        <Alert
          severity={message.type}
          onClose={() => setMessage(null)}
          sx={{ mb: 2 }}
        >
          {message.text}
        </Alert>
      )}

      {creating && <LinearProgress sx={{ mb: 2 }} />}

      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReceiptLongIcon color="primary" />
            รายการเบิกที่อนุมัติแล้ว (APPROVE) — สรุปตามผู้รับเงิน
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            กดปุ่ม &quot;สร้างรายการเตรียมจ่ายทั้งหมด&quot; เพื่อสร้างรายการจ่ายเงินรายผู้รับเงิน และเปลี่ยนสถานะเบิกเป็น &quot;เตรียมจ่าย&quot;
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            รายการที่แสดงคือรายการที่ผู้อนุมัติ &quot;อนุมัติให้จ่ายเงินได้&quot; แล้ว และอนุมัติมาแล้วอย่างน้อย 1 วัน (อนุมัติวันนี้จะแสดงในวันถัดไป)
          </Typography>

          {groupedByPayee.length === 0 ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              ไม่มีรายการเบิกที่พร้อมเตรียมจ่ายในขณะนี้ (รายการที่เพิ่งอนุมัติวันนี้จะแสดงในวันถัดไป)
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08) }}>
                    <TableCell sx={{ fontWeight: 600 }}>ลำดับ</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>ชื่อผู้รับเงิน</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>ธนาคาร</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>เลขบัญชี</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>จำนวนรายการ</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>ยอดรวม (บาท)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groupedByPayee.map((row, index) => (
                    <TableRow key={row.key} hover>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PersonIcon fontSize="small" color="action" />
                          {row.payee.name}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <AccountBalanceIcon fontSize="small" color="action" />
                          {getBankLabel(row.payee.bank)}
                        </Box>
                      </TableCell>
                      <TableCell>{row.payee.account_number}</TableCell>
                      <TableCell align="right">
                        <Chip label={`${row.count} รายการ`} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        ฿{formatMoney(row.totalAmount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

NewPayment.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  // ถ้าส่ง onClose มา (ใช้เป็น modal จาก Payment.js) จะโชว์ปุ่มปิดแทน BackButton และเรียก
  // onClose(created) แทน history.push ตอนสร้างรายการสำเร็จ — created=true ให้ผู้เรียก refresh list
  onClose: PropTypes.func,
};

NewPayment.defaultProps = {
  onClose: null,
};
